/**
 * I3: Feature Not Available Page
 * 
 * Neutral 404-style page shown when user navigates to a route
 * whose feature flag is disabled.
 * 
 * Requirements:
 * - No hint of what the feature was
 * - No future-availability messaging
 * - Consistent with backend's 404-not-403 principle (don't leak existence)
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Shield, ArrowLeft } from 'lucide-react';

const FeatureNotAvailable = () => {
  const { user } = useAuth();
  
  // Determine where to send user back
  const dashboardPath = user?.is_superadmin 
    ? '/founder/dashboard'
    : user?.is_admin 
      ? '/admin/command-center'
      : user?.account_type === 'employer' 
        ? '/employer/dashboard' 
        : '/employee/dashboard';

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--vett-navy, #F5F8FF)' }}
      data-testid="feature-not-available-page"
    >
      <div className="max-w-md w-full text-center">
        {/* Logo */}
        <div className="mb-8">
          <div 
            className="w-16 h-16 mx-auto rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #1B5AFF 0%, #FF6B6B 100%)' }}
          >
            <Shield className="h-8 w-8 text-white" />
          </div>
        </div>
        
        {/* Message - Neutral, no feature hints */}
        <h1 
          className="text-2xl font-bold mb-4"
          style={{ color: 'var(--vett-gray-light, #0A0F1E)' }}
        >
          Page Not Found
        </h1>
        
        <p 
          className="mb-8 text-base"
          style={{ color: 'var(--vett-gray-medium, #5B6472)' }}
        >
          The page you are looking for does not exist or you do not have access to it.
        </p>
        
        {/* Action */}
        <Link to={dashboardPath}>
          <Button
            className="inline-flex items-center gap-2"
            style={{ 
              background: 'linear-gradient(135deg, #1B5AFF 0%, #FF6B6B 100%)',
              color: 'white'
            }}
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default FeatureNotAvailable;
